// ============================================================================
// WIDGET REGISTRY - Static registry of all available second screen widgets
// Each widget registers its metadata and component here
// ============================================================================

import {
  Users, Clock, Navigation, BarChart3, Trophy, ListOrdered, Gauge, LayoutGrid,
  Activity, Layers, CreditCard, Sparkles, ScrollText, Cloud, Ticket, Brain,
  StickyNote, BookMarked, ShieldAlert, Map, Smartphone, Lightbulb, Network,
  Pin, Hand, CalendarDays, UserCircle, UserCheck, Clock4, BookOpen, LayoutList,
  Diamond,
} from 'lucide-react';
import type { WidgetDefinition } from '../../../types/second-screen.types';
import AvatarRingWidget from './AvatarRingWidget';
import TimerWidget from './TimerWidget';
import CurrentPhaseWidget from './CurrentPhaseWidget';
import StatsBarWidget from './StatsBarWidget';
import AvatarScoreboardWidget from './AvatarScoreboardWidget';
import WhosNextWidget from './WhosNextWidget';
import EngagementGaugeWidget from './EngagementGaugeWidget';
import EngagementHeatmapWidget from './EngagementHeatmapWidget';
import PulseIndicatorWidget from './PulseIndicatorWidget';
import FocusViewWidget from './FocusViewWidget';
import PromptCardWidget from './PromptCardWidget';
import SpotlightWidget from './SpotlightWidget';
import TranscriptCrawlWidget from './TranscriptCrawlWidget';
import WordCloudWidget from './WordCloudWidget';
import RequirementsTickerWidget from './RequirementsTickerWidget';
import AiInsightsFeedWidget from './AiInsightsFeedWidget';
import AssumptionWallWidget from './AssumptionWallWidget';
import DecisionLogWidget from './DecisionLogWidget';
import GovernanceRadarWidget from './GovernanceRadarWidget';
import SessionJourneyMapWidget from './SessionJourneyMapWidget';
import DeviceStatusWidget from './DeviceStatusWidget';
import InspirationWallWidget from './InspirationWallWidget';
import ProjectGraphWidget from './ProjectGraphWidget';
import TaskContentWidget from './TaskContentWidget';
import WelcomeHeaderWidget from './WelcomeHeaderWidget';
import AgendaWidget from './AgendaWidget';
import AttendeeListWidget from './AttendeeListWidget';
import ParticipantPanelWidget from './ParticipantPanelWidget';
import ClockWidget from './ClockWidget';
import PersonasWidget from './PersonasWidget';
import UserStoriesWidget from './UserStoriesWidget';
import AmbientBackgroundWidget from './AmbientBackgroundWidget';
import RequirementsMirrorWidget from './RequirementsMirrorWidget';
import DoubleDiamondWidget from './DoubleDiamondWidget';

// Widget registry — populated as widgets are implemented
export const WIDGET_REGISTRY: Record<string, WidgetDefinition> = {};

// Helper to register a widget
export function registerWidget(definition: WidgetDefinition) {
  WIDGET_REGISTRY[definition.id] = definition;
}

// Widget IDs as constants for type safety
export const WIDGET_IDS = {
  AVATAR_RING: 'avatar-ring',
  TIMER: 'timer',
  CURRENT_PHASE: 'current-phase',
  STATS_BAR: 'stats-bar',
  AVATAR_SCOREBOARD: 'avatar-scoreboard',
  WHOS_NEXT: 'whos-next',
  ENGAGEMENT_GAUGE: 'engagement-gauge',
  ENGAGEMENT_HEATMAP: 'engagement-heatmap',
  PULSE_INDICATOR: 'pulse-indicator',
  FOCUS_VIEW: 'focus-view',
  PROMPT_CARD: 'prompt-card',
  SPOTLIGHT: 'spotlight',
  TRANSCRIPT_CRAWL: 'transcript-crawl',
  WORD_CLOUD: 'word-cloud',
  REQUIREMENTS_TICKER: 'requirements-ticker',
  AI_INSIGHTS_FEED: 'ai-insights-feed',
  ASSUMPTION_WALL: 'assumption-wall',
  DECISION_LOG: 'decision-log',
  GOVERNANCE_RADAR: 'governance-radar',
  SESSION_JOURNEY_MAP: 'session-journey-map',
  DEVICE_STATUS: 'device-status',
  INSPIRATION_WALL: 'inspiration-wall',
  PROJECT_GRAPH: 'project-graph',
  TASK_CONTENT: 'task-content',
  WELCOME_HEADER: 'welcome-header',
  AGENDA: 'agenda',
  ATTENDEE_LIST: 'attendee-list',
  PARTICIPANT_PANEL: 'participant-panel',
  CLOCK: 'clock',
  PERSONAS: 'personas',
  USER_STORIES: 'user-stories',
  AMBIENT_BACKGROUND: 'ambient-background',
  REQUIREMENTS_MIRROR: 'requirements-mirror',
  DOUBLE_DIAMOND: 'double-diamond',
} as const;

// ============================================================================
// Core Widgets
// ============================================================================

registerWidget({
  id: WIDGET_IDS.AVATAR_RING,
  label: 'Avatar Ring',
  description: 'Circular avatars with animated status rings (speaking/thinking/idle)',
  icon: Users,
  category: 'avatars',
  defaultW: 8,
  defaultH: 2,
  minW: 4,
  minH: 2,
  component: AvatarRingWidget,
});

registerWidget({
  id: WIDGET_IDS.TIMER,
  label: 'Timer',
  description: 'Session elapsed time with large monospace digits',
  icon: Clock,
  category: 'session',
  defaultW: 4,
  defaultH: 2,
  minW: 2,
  minH: 1,
  component: TimerWidget,
});

registerWidget({
  id: WIDGET_IDS.CURRENT_PHASE,
  label: 'Current Phase',
  description: 'Phase name, objectives, and horizontal progress timeline',
  icon: Navigation,
  category: 'session',
  defaultW: 12,
  defaultH: 2,
  minW: 6,
  minH: 2,
  component: CurrentPhaseWidget,
});

registerWidget({
  id: WIDGET_IDS.STATS_BAR,
  label: 'Stats Bar',
  description: 'Stat cards: transcripts, contributions, acceptance rate, personas, stories',
  icon: BarChart3,
  category: 'data',
  defaultW: 12,
  defaultH: 2,
  minW: 6,
  minH: 1,
  component: StatsBarWidget,
});

// ============================================================================
// Avatar & Engagement Widgets
// ============================================================================

registerWidget({
  id: WIDGET_IDS.AVATAR_SCOREBOARD,
  label: 'Scoreboard',
  description: 'Live leaderboard of contributions per avatar',
  icon: Trophy,
  category: 'avatars',
  defaultW: 4,
  defaultH: 3,
  minW: 3,
  minH: 2,
  component: AvatarScoreboardWidget,
});

registerWidget({
  id: WIDGET_IDS.WHOS_NEXT,
  label: "Who's Next",
  description: 'Pending contributions queue with urgency indicators',
  icon: ListOrdered,
  category: 'avatars',
  defaultW: 4,
  defaultH: 3,
  minW: 3,
  minH: 2,
  component: WhosNextWidget,
});

registerWidget({
  id: WIDGET_IDS.ENGAGEMENT_GAUGE,
  label: 'Engagement',
  description: 'Arc gauge from conversation health balance score',
  icon: Gauge,
  category: 'engagement',
  defaultW: 3,
  defaultH: 2,
  minW: 2,
  minH: 2,
  component: EngagementGaugeWidget,
});

registerWidget({
  id: WIDGET_IDS.ENGAGEMENT_HEATMAP,
  label: 'Heatmap',
  description: 'Avatar activity density over time blocks',
  icon: LayoutGrid,
  category: 'engagement',
  defaultW: 6,
  defaultH: 3,
  minW: 4,
  minH: 2,
  component: EngagementHeatmapWidget,
});

registerWidget({
  id: WIDGET_IDS.PULSE_INDICATOR,
  label: 'Pulse',
  description: 'Activity rate indicator with pulsing animation',
  icon: Activity,
  category: 'engagement',
  defaultW: 3,
  defaultH: 2,
  minW: 2,
  minH: 2,
  component: PulseIndicatorWidget,
});

// ============================================================================
// Content & Focus Widgets
// ============================================================================

registerWidget({
  id: WIDGET_IDS.FOCUS_VIEW,
  label: 'Focus View',
  description: 'Switchable view: tasks, transcript, activities, Miro embed',
  icon: Layers,
  category: 'content',
  defaultW: 6,
  defaultH: 4,
  minW: 4,
  minH: 3,
  component: FocusViewWidget,
  configSchema: [
    { key: 'defaultTab', label: 'Default Tab', type: 'select', options: [
      { value: 'transcript', label: 'Transcript' },
      { value: 'tasks', label: 'Tasks' },
      { value: 'activities', label: 'Activities' },
      { value: 'miro', label: 'Miro' },
    ], defaultValue: 'transcript' },
    { key: 'miroUrl', label: 'Miro Board URL', type: 'url', defaultValue: '' },
  ],
});

registerWidget({
  id: WIDGET_IDS.PROMPT_CARD,
  label: 'Prompt Card',
  description: 'Flip card with prompt and facilitation guide, auto-cycles',
  icon: CreditCard,
  category: 'content',
  defaultW: 4,
  defaultH: 3,
  minW: 3,
  minH: 3,
  component: PromptCardWidget,
  configSchema: [
    { key: 'deckMode', label: 'Deck', type: 'select', options: [
      { value: 'phase', label: 'Current Phase' },
      { value: 'standalone', label: 'All Standalone' },
      { value: '8', label: 'Challenge Reframe' },
      { value: '9', label: 'Assumption Buster' },
    ], defaultValue: 'phase' },
  ],
});

registerWidget({
  id: WIDGET_IDS.SPOTLIGHT,
  label: 'Spotlight',
  description: 'Displays the currently spotlighted contribution',
  icon: Sparkles,
  category: 'content',
  defaultW: 4,
  defaultH: 3,
  minW: 3,
  minH: 2,
  component: SpotlightWidget,
});

registerWidget({
  id: WIDGET_IDS.TRANSCRIPT_CRAWL,
  label: 'Transcript Crawl',
  description: 'Bloomberg-style scrolling ticker of recent transcript',
  icon: ScrollText,
  category: 'content',
  defaultW: 12,
  defaultH: 1,
  minW: 6,
  minH: 1,
  component: TranscriptCrawlWidget,
});

// ============================================================================
// Data & Intelligence Widgets
// ============================================================================

registerWidget({
  id: WIDGET_IDS.WORD_CLOUD,
  label: 'Word Cloud',
  description: 'Real-time word cloud from transcript themes',
  icon: Cloud,
  category: 'data',
  defaultW: 4,
  defaultH: 3,
  minW: 3,
  minH: 2,
  component: WordCloudWidget,
});

registerWidget({
  id: WIDGET_IDS.REQUIREMENTS_TICKER,
  label: 'Requirements',
  description: 'Scrolling ticker of extracted personas and user stories',
  icon: Ticket,
  category: 'data',
  defaultW: 12,
  defaultH: 1,
  minW: 6,
  minH: 1,
  component: RequirementsTickerWidget,
});

registerWidget({
  id: WIDGET_IDS.AI_INSIGHTS_FEED,
  label: 'AI Insights',
  description: 'Feed of guidance highlights, missing perspectives, health alerts',
  icon: Brain,
  category: 'data',
  defaultW: 4,
  defaultH: 3,
  minW: 3,
  minH: 2,
  component: AiInsightsFeedWidget,
});

registerWidget({
  id: WIDGET_IDS.ASSUMPTION_WALL,
  label: 'Assumptions',
  description: 'Grid of assumptions surfaced during the session',
  icon: StickyNote,
  category: 'data',
  defaultW: 4,
  defaultH: 3,
  minW: 3,
  minH: 2,
  component: AssumptionWallWidget,
});

registerWidget({
  id: WIDGET_IDS.DECISION_LOG,
  label: 'Decisions',
  description: 'Timestamped log of accepted contributions',
  icon: BookMarked,
  category: 'data',
  defaultW: 4,
  defaultH: 3,
  minW: 3,
  minH: 2,
  component: DecisionLogWidget,
});

registerWidget({
  id: WIDGET_IDS.GOVERNANCE_RADAR,
  label: 'Governance',
  description: 'Credo.ai risk classification gauge',
  icon: ShieldAlert,
  category: 'data',
  defaultW: 3,
  defaultH: 2,
  minW: 2,
  minH: 2,
  component: GovernanceRadarWidget,
});

// ============================================================================
// Session Overview Widgets
// ============================================================================

registerWidget({
  id: WIDGET_IDS.SESSION_JOURNEY_MAP,
  label: 'Journey Map',
  description: 'Horizontal timeline of phases with progress markers',
  icon: Map,
  category: 'session',
  defaultW: 12,
  defaultH: 2,
  minW: 6,
  minH: 2,
  component: SessionJourneyMapWidget,
});

registerWidget({
  id: WIDGET_IDS.DEVICE_STATUS,
  label: 'Devices',
  description: 'Physical device cards with battery and connection state',
  icon: Smartphone,
  category: 'session',
  defaultW: 4,
  defaultH: 3,
  minW: 3,
  minH: 2,
  component: DeviceStatusWidget,
});

// ============================================================================
// Inspiration Wall Widget
// ============================================================================

registerWidget({
  id: WIDGET_IDS.INSPIRATION_WALL,
  label: 'Inspiration Wall',
  description: 'Floating cards of real news, social quotes, and AI persona perspectives',
  icon: Lightbulb,
  category: 'content',
  defaultW: 12,
  defaultH: 8,
  minW: 6,
  minH: 4,
  component: InspirationWallWidget,
  configSchema: [
    {
      key: 'rotationInterval',
      label: 'Card Rotation (seconds)',
      type: 'select',
      options: [
        { value: '3', label: '3 seconds' },
        { value: '5', label: '5 seconds' },
        { value: '8', label: '8 seconds' },
        { value: '12', label: '12 seconds' },
      ],
      defaultValue: '5',
    },
    {
      key: 'maxVisibleCards',
      label: 'Max Visible Cards',
      type: 'select',
      options: [
        { value: '4', label: '4 cards' },
        { value: '6', label: '6 cards' },
        { value: '8', label: '8 cards' },
      ],
      defaultValue: '6',
    },
  ],
});

// ============================================================================
// Knowledge Graph Widget
// ============================================================================

registerWidget({
  id: WIDGET_IDS.PROJECT_GRAPH,
  label: 'Knowledge Graph',
  description: 'Animated force-directed knowledge graph with entity types, particles, and gentle drift',
  icon: Network,
  category: 'data',
  defaultW: 12,
  defaultH: 8,
  minW: 6,
  minH: 4,
  component: ProjectGraphWidget,
});

// ============================================================================
// Task Content Widget (Pinned Images / Markdown)
// ============================================================================

registerWidget({
  id: WIDGET_IDS.TASK_CONTENT,
  label: 'Task Content',
  description: 'Displays images, markdown, and diagrams pushed from session task outputs',
  icon: Pin,
  category: 'content',
  defaultW: 6,
  defaultH: 4,
  minW: 3,
  minH: 2,
  component: TaskContentWidget,
});

// ============================================================================
// Welcome Screen Widgets
// ============================================================================

registerWidget({
  id: WIDGET_IDS.WELCOME_HEADER,
  label: 'Welcome Header',
  description: 'Large welcome message with customer name and workshop goal',
  icon: Hand,
  category: 'session',
  defaultW: 9,
  defaultH: 3,
  minW: 4,
  minH: 2,
  component: WelcomeHeaderWidget,
  configSchema: [
    { key: 'customerName', label: 'Customer Name', type: 'text', defaultValue: '' },
    { key: 'goalText', label: 'Workshop Goal', type: 'text', defaultValue: '' },
  ],
});

registerWidget({
  id: WIDGET_IDS.AGENDA,
  label: 'Agenda',
  description: 'Vertical timeline of workshop phases with current phase highlighted',
  icon: CalendarDays,
  category: 'session',
  defaultW: 3,
  defaultH: 5,
  minW: 2,
  minH: 3,
  component: AgendaWidget,
});

registerWidget({
  id: WIDGET_IDS.ATTENDEE_LIST,
  label: 'Attendees',
  description: 'Display list of session attendees with roles and organizations',
  icon: UserCircle,
  category: 'session',
  defaultW: 9,
  defaultH: 3,
  minW: 4,
  minH: 2,
  component: AttendeeListWidget,
});

registerWidget({
  id: WIDGET_IDS.PARTICIPANT_PANEL,
  label: 'Participants',
  description: 'Combined view of human attendees and AI avatars',
  icon: UserCheck,
  category: 'avatars',
  defaultW: 3,
  defaultH: 8,
  minW: 2,
  minH: 4,
  component: ParticipantPanelWidget,
});

registerWidget({
  id: WIDGET_IDS.CLOCK,
  label: 'Clock',
  description: 'Current time of day in large format',
  icon: Clock4,
  category: 'session',
  defaultW: 3,
  defaultH: 3,
  minW: 2,
  minH: 2,
  component: ClockWidget,
});

// ============================================================================
// Requirements Widgets (Personas & User Stories)
// ============================================================================

registerWidget({
  id: WIDGET_IDS.PERSONAS,
  label: 'Personas',
  description: 'Extracted user personas with goals and pain points',
  icon: Users,
  category: 'data',
  defaultW: 6,
  defaultH: 4,
  minW: 4,
  minH: 3,
  component: PersonasWidget,
});

registerWidget({
  id: WIDGET_IDS.USER_STORIES,
  label: 'User Stories',
  description: 'Extracted user stories with priority and acceptance criteria',
  icon: BookOpen,
  category: 'data',
  defaultW: 6,
  defaultH: 4,
  minW: 4,
  minH: 3,
  component: UserStoriesWidget,
});

// ============================================================================
// Requirements Mirror Widget
// ============================================================================

registerWidget({
  id: WIDGET_IDS.REQUIREMENTS_MIRROR,
  label: 'Requirements Mirror',
  description: 'Full-screen persona + user stories mirror — synced with facilitator\'s active tab and scroll',
  icon: LayoutList,
  category: 'content',
  defaultW: 12,
  defaultH: 8,
  minW: 6,
  minH: 4,
  component: RequirementsMirrorWidget,
});

// ============================================================================
// Double Diamond Widget
// ============================================================================

registerWidget({
  id: WIDGET_IDS.DOUBLE_DIAMOND,
  label: 'Double Diamond',
  description: 'AI-assessed workshop progress mapped to the Double Diamond framework — fills left-to-right with milestone callouts',
  icon: Diamond,
  category: 'session',
  defaultW: 12,
  defaultH: 3,
  minW: 6,
  minH: 2,
  component: DoubleDiamondWidget,
  configSchema: [
    {
      key: 'cinematic',
      label: 'Cinematic Mode',
      type: 'boolean',
      defaultValue: false,
    },
  ],
});

// ============================================================================
// Ambient Background Widget
// ============================================================================

registerWidget({
  id: WIDGET_IDS.AMBIENT_BACKGROUND,
  label: 'Ambient Background',
  description: 'Reactive neural network canvas — avatar nodes pulse with session activity',
  icon: Network,
  category: 'data',
  transparent: true,
  defaultW: 12,
  defaultH: 8,
  minW: 6,
  minH: 4,
  component: AmbientBackgroundWidget,
  configSchema: [
    {
      key: 'intensity',
      label: 'Intensity',
      type: 'select',
      options: [
        { value: 'subtle', label: 'Subtle' },
        { value: 'moderate', label: 'Moderate' },
        { value: 'vivid', label: 'Vivid' },
      ],
      defaultValue: 'moderate',
    },
  ],
});
